// src/components/NotificationBell.js
import React, { useState, useEffect, useRef } from 'react';
import {
  collection, query, orderBy, limit,
  onSnapshot, updateDoc, doc, writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen]                   = useState(false);
  const [loading, setLoading]             = useState(true);
  const panelRef                          = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // ── Real-time listener ────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(list);
      setLoading(false);

      // Play subtle sound on new unread (optional — won't break if blocked)
      const prevUnread = notifications.filter(n => !n.read).length;
      const newUnread  = list.filter(n => !n.read).length;
      if (newUnread > prevUnread && !open) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 880;
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        } catch (e) {}
      }
    });

    return () => unsub();
  }, []);

  // ── Close panel on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ── Mark single as read ───────────────────────────────────────────────────
  const markRead = async (notif) => {
    if (notif.read) return;
    try {
      await updateDoc(doc(db, 'notifications', notif.id), { read: true });
    } catch (e) {}
  };

  // ── Mark all as read ──────────────────────────────────────────────────────
  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach(n => batch.update(doc(db, 'notifications', n.id), { read: true }));
      await batch.commit();
    } catch (e) {}
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return '';
    const d   = ts.toDate ? ts.toDate() : new Date(ts);
    const sec = Math.floor((new Date() - d) / 1000);
    if (sec < 60)   return 'Just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };

  const typeLabel = (type) => ({
    service_submitted: 'Service',
    emergency_service: 'Emergency',
    photo_flagged:     'Photo Flag',
    fault_raised:      'Fault'
  }[type] || 'Alert');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={panelRef} style={s.wrapper}>

      {/* ── Floating Bell Button ── */}
      <button
        style={{
          ...s.bell,
          background: unreadCount > 0
            ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
            : 'rgba(15,23,42,0.95)',
          boxShadow: unreadCount > 0
            ? '0 0 0 3px rgba(99,102,241,0.3), 0 8px 32px rgba(0,0,0,0.4)'
            : '0 8px 32px rgba(0,0,0,0.4)'
        }}
        onClick={() => setOpen(prev => !prev)}
        title="Notifications"
      >
        <span style={{ fontSize: 22, animation: unreadCount > 0 ? 'bellRing 1.5s ease infinite' : 'none' }}>
          🔔
        </span>
        {unreadCount > 0 && (
          <span style={s.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Notification Panel ── */}
      {open && (
        <div style={s.panel}>

          {/* Panel header */}
          <div style={s.panelHeader}>
            <div>
              <h3 style={s.panelTitle}>Notifications</h3>
              <p style={s.panelSub}>
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up ✓'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {unreadCount > 0 && (
                <button style={s.markAllBtn} onClick={markAllRead}>
                  ✓ All read
                </button>
              )}
              <button style={s.closeBtn} onClick={() => setOpen(false)}>✕</button>
            </div>
          </div>

          {/* Notification list */}
          <div style={s.list}>
            {loading ? (
              <div style={s.empty}>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔕</div>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  style={{
                    ...s.notifItem,
                    background: notif.read
                      ? 'transparent'
                      : 'rgba(99,102,241,0.07)',
                    borderLeft: notif.read
                      ? '3px solid transparent'
                      : `3px solid ${notif.color || '#6366f1'}`
                  }}
                  onClick={() => markRead(notif)}
                >
                  {/* Icon */}
                  <div style={{
                    ...s.notifIcon,
                    background: `${notif.color || '#6366f1'}22`,
                    border: `1px solid ${notif.color || '#6366f1'}44`
                  }}>
                    {notif.icon || '📋'}
                  </div>

                  {/* Content */}
                  <div style={s.notifContent}>
                    <div style={s.notifHeader}>
                      <span style={{
                        ...s.notifType,
                        color: notif.color || '#6366f1',
                        background: `${notif.color || '#6366f1'}22`
                      }}>
                        {typeLabel(notif.type)}
                      </span>
                      {!notif.read && <span style={s.unreadDot} />}
                    </div>
                    <p style={s.notifTitle}>{notif.title}</p>
                    <p style={s.notifMsg}>{notif.message}</p>
                    <p style={s.notifTime}>{formatTimeAgo(notif.createdAt)}</p>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      )}

      {/* Bell ring animation */}
      <style>{`
        @keyframes bellRing {
          0%, 100% { transform: rotate(0deg); }
          10%       { transform: rotate(15deg); }
          20%       { transform: rotate(-15deg); }
          30%       { transform: rotate(10deg); }
          40%       { transform: rotate(-10deg); }
          50%       { transform: rotate(5deg); }
          60%       { transform: rotate(-5deg); }
          70%       { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  wrapper: {
    position:  'fixed',
    bottom:    24,
    right:     24,
    zIndex:    9000,
    fontFamily: "'Inter', -apple-system, sans-serif"
  },
  bell: {
    width:        60,
    height:       60,
    borderRadius: '50%',
    border:       '1px solid rgba(255,255,255,0.15)',
    cursor:       'pointer',
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
    position:     'relative',
    transition:   'all 0.3s ease'
  },
  badge: {
    position:   'absolute',
    top:        -4,
    right:      -4,
    background: '#ef4444',
    color:      '#fff',
    fontSize:   10,
    fontWeight: 700,
    minWidth:   18,
    height:     18,
    borderRadius: 9,
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding:    '0 4px',
    border:     '2px solid #0f172a'
  },
  panel: {
    position:     'absolute',
    bottom:       70,
    right:        0,
    width:        340,
    maxHeight:    520,
    background:   'rgba(15,23,42,0.98)',
    border:       '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    boxShadow:    '0 24px 64px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(20px)',
    display:      'flex',
    flexDirection: 'column',
    overflow:     'hidden',
    animation:    'slideUp 0.2s ease-out'
  },
  panelHeader: {
    padding:      '16px 16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display:      'flex',
    justifyContent: 'space-between',
    alignItems:   'flex-start',
    flexShrink:   0
  },
  panelTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: '#fff' },
  panelSub:   { margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  markAllBtn: {
    background:   'rgba(99,102,241,0.15)',
    border:       '1px solid rgba(99,102,241,0.3)',
    borderRadius: 8,
    padding:      '5px 10px',
    color:        '#a5b4fc',
    fontSize:     12,
    cursor:       'pointer',
    fontWeight:   600
  },
  closeBtn: {
    background:   'rgba(255,255,255,0.08)',
    border:       'none',
    borderRadius: 8,
    padding:      '5px 10px',
    color:        '#fff',
    fontSize:     13,
    cursor:       'pointer'
  },
  list: {
    overflowY: 'auto',
    flex:      1
  },
  empty: {
    textAlign: 'center',
    padding:   '40px 20px',
    color:     'rgba(255,255,255,0.3)',
    fontSize:  13
  },
  notifItem: {
    padding:    '12px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display:    'flex',
    gap:        12,
    cursor:     'pointer',
    transition: 'background 0.15s'
  },
  notifIcon: {
    width:        38,
    height:       38,
    borderRadius: 10,
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
    fontSize:     18,
    flexShrink:   0
  },
  notifContent: { flex: 1, minWidth: 0 },
  notifHeader:  { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 },
  notifType: {
    fontSize:     10,
    fontWeight:   700,
    padding:      '2px 7px',
    borderRadius: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  unreadDot: {
    width:        6,
    height:       6,
    borderRadius: '50%',
    background:   '#6366f1',
    marginLeft:   'auto'
  },
  notifTitle: {
    margin:     '0 0 2px',
    fontSize:   13,
    fontWeight: 600,
    color:      '#e2e8f0',
    whiteSpace: 'nowrap',
    overflow:   'hidden',
    textOverflow: 'ellipsis'
  },
  notifMsg: {
    margin:     '0 0 4px',
    fontSize:   12,
    color:      'rgba(255,255,255,0.5)',
    lineHeight: 1.4,
    display:    '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow:   'hidden'
  },
  notifTime: {
    margin:   0,
    fontSize: 11,
    color:    'rgba(255,255,255,0.25)'
  }
};

export default NotificationBell;
